import { Label } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import classNames from 'classnames';
import React from 'react';
import './WorksheetGrid.scss';
import { CellComponent, WorksheetGridProps } from './types';

/**
 * Helper that returns the element to place in a cell given the component definition.
 */
const getCellElement = (component: CellComponent, worksheetId: string) => {
  let baseElement = typeof component === 'function' ? component(worksheetId) : component;
  return React.cloneElement(baseElement, {
    inGrid: true,
  });
};

/**
 * Provides a grid where each row represents a different worksheet, filling the horizontal space provided
 *
 * Not to be confused with Components/WorksheetGrids/WorksheetGrid - the original component
 * for the dry mass entry grid (to be reworked to utilise this component in future.)
 *
 * Typical usages:
 * * Selecting from a list of worksheets (e.g. batch update page)
 * * Bulk entry grids (TBA)
 *
 * Limitations:
 * * Currently renders full grid. Could use a library like react-virtualized to only render visible portion.
 */
let WorksheetGrid: React.FC<WorksheetGridProps> = ({
  identifierColumns,
  fieldColumns,
  worksheetIds,
  WorksheetPropsProvider,
  HeaderPropsProvider,
}) => {
  /**
   * We need to track the current "left" value of the identifier columns
   * so we can stick these columns to that position
   */
  let identifierCurrentLeft = 0;

  return (
    <div
      className="WorksheetGrid"
      style={{
        gridTemplateColumns:
          // Fixed width column for each column shown
          [...identifierColumns, ...fieldColumns]
            .filter((c) => !c.hidden)
            .map((c) => `${c.width}px`)
            .join(' ') +
          // Filler column
          ' auto',
      }}
    >
      <HeaderPropsProvider>
        {identifierColumns
          .filter((c) => !c.hidden)
          .map((c, i) => {
            const left = identifierCurrentLeft;
            identifierCurrentLeft += c.width;
            return (
              <Label
                className={classNames(
                  'WorksheetGrid-Cell',
                  'WorksheetGrid-Cell-header',
                  'WorksheetGrid-Cell-identifier'
                )}
                style={{
                  ...c.additionalCellStyle,
                  left,
                }}
                emph
                key={i}
              >
                {c.header}
              </Label>
            );
          })}
        {fieldColumns
          .filter((c) => !c.hidden)
          .map((c, i) => {
            return (
              <Label
                className={classNames('WorksheetGrid-Cell', 'WorksheetGrid-Cell-header')}
                style={{
                  ...c.additionalCellStyle,
                }}
                emph
                key={i}
              >
                {c.header}
              </Label>
            );
          })}
        <div className={classNames('WorksheetGrid-Cell', 'WorksheetGrid-Cell-header', 'WorksheetGrid-Cell-filler')} />
      </HeaderPropsProvider>
      {worksheetIds.map((w, i) => (
        <WorksheetPropsProvider worksheetId={w} key={i}>
          {identifierColumns
            .filter((c) => !c.hidden)
            .map((c, i) => {
              if (i === 0) identifierCurrentLeft = 0;
              const left = identifierCurrentLeft;
              identifierCurrentLeft += c.width;

              return (
                <div
                  className={classNames('WorksheetGrid-Cell', 'WorksheetGrid-Cell-identifier')}
                  style={{ ...c.additionalCellStyle, left }}
                  key={i}
                >
                  {getCellElement(c.component, w)}
                </div>
              );
            })}
          {fieldColumns
            .filter((c) => !c.hidden)
            .map((c, i) => (
              <div className={classNames('WorksheetGrid-Cell')} style={{ ...c.additionalCellStyle }} key={i}>
                {getCellElement(c.component, w)}
              </div>
            ))}
          <div className={classNames('WorksheetGrid-Cell', 'WorksheetGrid-Cell-filler')} />
        </WorksheetPropsProvider>
      ))}
    </div>
  );
};

WorksheetGrid = withInjectedProps(WorksheetGrid);

export default WorksheetGrid;
export { WorksheetGrid };
